import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CFormInput } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSearch, cilChevronBottom, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import './SearchableSelect.scss';

const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select...',
  disabled = false,
  invalid = false,
  size = 'md',
  className = '',
  id = '',
  name = '',
  emptyText = 'No options found',
  displayKey = 'Name',
  valueKey = 'Guid',
  searchKeys = null,
  onSearchTermChange = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const portalDropdownRef = useRef(null);
  const onSearchTermChangeRef = useRef(onSearchTermChange);
  const lastSentSearchTermRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = 
  useState({ top: 0, left: 0, width: 0, maxHeight: 300, placement: 'bottom' });
  const searchInputRef = useRef(null);

  useEffect(() => {
    onSearchTermChangeRef.current = onSearchTermChange;
  }, [onSearchTermChange]);

  const getDisplayText = () => {
    if (!value) return '';
    const selectedOption = options.find(option => option[valueKey] === value);
    return selectedOption ? selectedOption[displayKey] : '';
  };

  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const normalizedSearch = searchTerm.toLocaleLowerCase('tr-TR');
      const activeSearchKeys = Array.isArray(searchKeys) && searchKeys.length > 0
        ? searchKeys
        : [displayKey];

      const filtered = options.filter(option =>
        activeSearchKeys.some((key) => String(option?.[key] ?? '')
          .toLocaleLowerCase('tr-TR')
          .includes(normalizedSearch))
      );
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm, options, displayKey, searchKeys]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (typeof onSearchTermChangeRef.current !== 'function') return undefined;
    if (lastSentSearchTermRef.current === searchTerm) return undefined;

    const timer = setTimeout(() => {
      onSearchTermChangeRef.current?.(searchTerm);
      lastSentSearchTermRef.current = searchTerm;
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideTrigger = triggerRef.current && triggerRef.current.contains(event.target);
      const clickedInsidePortal = portalDropdownRef.current &&
       portalDropdownRef.current.contains(event.target);
      if (!clickedInsideTrigger && !clickedInsidePortal) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateDropdownPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const estimatedHeight = Math.min(300, viewportHeight * 0.5);
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placeBelow = spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove;

    let top = placeBelow ? rect.bottom : rect.top - estimatedHeight;
    let maxHeight = placeBelow ? Math.max(120, spaceBelow - 10) : Math.max(120, spaceAbove - 10);

    if (portalDropdownRef.current) {
      const measuredHeight = Math.min(portalDropdownRef.current.offsetHeight 
        || estimatedHeight, 300);
      if (!placeBelow) {
        top = Math.max(10, rect.top - measuredHeight);
        maxHeight = Math.max(120, spaceAbove - 10);
      } else {
        top = Math.min(rect.bottom, viewportHeight - 10);
        maxHeight = Math.max(120, spaceBelow - 10);
      }
    }

    setDropdownPosition({
      top,
      left: rect.left,
      width: rect.width,
      maxHeight,
      placement: placeBelow ? 'bottom' : 'top'
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();
    const handleScroll = () => updateDropdownPosition();
    const handleResize = () => updateDropdownPosition();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    const id = setTimeout(updateDropdownPosition, 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex][valueKey]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(prev => {
      const newState = !prev;
      if (newState) {
        lastSentSearchTermRef.current = null;
        setTimeout(() => searchInputRef.current?.focus(), 100);
      } else {
        setSearchTerm('');
      }
      return newState;
    });
  };

  return (
    <div 
      ref={rootRef}
      className={`searchable-select ${className} ${size} ${invalid ? 'is-invalid' : ''} 
      ${disabled ? 'disabled' : ''}`}
      id={id}
      data-name={name}
    >
      <div 
        className={`select-trigger ${isOpen ? 'open' : ''} ${value ? 'has-value' : ''}`}
        ref={triggerRef}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="select-value">
          {getDisplayText() || (
            <span>{(placeholder && placeholder.trim()) 
              ? placeholder : 'Select...'}</span>
          )}
        </span>
        <div className="select-icons">
          {value && !disabled && (
            <button
              type="button"
              className="clear-btn"
              onClick={handleClear}
              tabIndex={-1}
              aria-label="Clear selection"
            >
              <CIcon icon={cilX} size="sm" />
            </button>
          )}
          <CIcon 
            icon={cilChevronBottom} 
            className={`chevron ${isOpen ? 'rotated' : ''}`}
            size="sm"
          />
        </div>
      </div>

      {isOpen && createPortal(
        (
          <div className="searchable-select searchable-select-portal">
            <div
              ref={portalDropdownRef}
              className="select-dropdown"
              style={{ position: 'fixed', top: dropdownPosition.top, left: dropdownPosition.left, 
              width: dropdownPosition.width, maxHeight: dropdownPosition.maxHeight, zIndex: 1050 }}
            >
              <div className="search-container">
                <CIcon icon={cilSearch} className="search-icon" size="sm" />
                <CFormInput
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="search-input"
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                />
              </div>
              <div className="options-container" role="listbox">
                {filteredOptions.length === 0 ? (
                  <div className="empty-option" role="option">
                    {emptyText}
                  </div>
                ) : (
                  filteredOptions.map((option, index) => (
                    <div
                      key={option[valueKey]}
                      className={`option-item ${highlightedIndex === index ? 'highlighted' : ''} 
                      ${value === option[valueKey] ? 'selected' : ''}`}
                      onClick={() => handleSelect(option[valueKey])}
                      role="option"
                      aria-selected={value === option[valueKey]}
                    >
                      {option[displayKey]}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ),
        document.body
      )}
    </div>
  );
};

SearchableSelect.propTypes = {
  options: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  emptyText: PropTypes.string,
  displayKey: PropTypes.string,
  valueKey: PropTypes.string,
  searchKeys: PropTypes.arrayOf(PropTypes.string),
  onSearchTermChange: PropTypes.func
};

export default SearchableSelect;
